import { useState, useEffect, useMemo } from 'react'
import { useQuery } from 'react-query'
import { fetchNaturaNCMList } from '../../../services'

/**
 * Hook to manage cache and search for NCMs
 * @returns {Object} Object with functions and states to manage NCMs
 */
export const useNCMCache = () => {
  const [searchTerm, setSearchTerm] = useState('')
  
  const { 
    data: ncmResponse, 
    isLoading, 
    error,
    refetch 
  } = useQuery(
    ['natura-ncm-list'], 
    fetchNaturaNCMList,
    {
      staleTime: 1000 * 60 * 30, 
      cacheTime: 1000 * 60 * 60, 
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000)
    }
  )

  const ncmList = useMemo(() => ncmResponse?.data || [], [ncmResponse])

  const filteredNCMs = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) {
      return []
    }

    const term = searchTerm.toLowerCase().trim()
    
    return ncmList.filter(ncm => {
      const codeMatch = ncm.code.toLowerCase().includes(term)
      const descriptionMatch = ncm.description ? ncm.description.toLowerCase().includes(term) : false
      
      return codeMatch || descriptionMatch
    }).slice(0, 10) 
  }, [ncmList, searchTerm])

  const findNCMByCode = (code) => {
    return ncmList.find(ncm => ncm.code === code) || null
  }

  const formatNCMOption = (ncm) => {
    return ncm.description ? `${ncm.code} - ${ncm.description}` : ncm.code
  }

  const isValidNCMCode = (code) => {
    return ncmList.some(ncm => ncm.code === code)
  }

  const addNCMToCache = (newNCM) => {
    refetch()
  }

  return {
    searchTerm,
    setSearchTerm,
    isLoading,
    error,
    ncmList,
    filteredNCMs,
    findNCMByCode,
    formatNCMOption,
    isValidNCMCode,
    refetch,
    addNCMToCache,
    totalNCMs: ncmList.length,
    filteredCount: filteredNCMs.length,
    hasMinSearchLength: searchTerm.length >= 2
  }
} 